// The one owner of the spend poll of the admin header: it polls at two speeds (fast while an AI run is queued or
// running, idle otherwise), keeps at most one request outstanding and stops for good on stop(). A hidden browser tab does
// not poll at all (the timer is cancelled and no request is sent; the browser's own slowing of background timers is not
// relied on) and one fetch plus a fresh timer follow as soon as the tab is visible again. Everything it needs is
// injected (the request, what to do with the answer, the rule that says whether a run is active, the scheduler, the
// page), so it is tested in plain node with a fake clock and Gatsby can build it: it touches no browser global.
// CommonJS, see src/lib/ai-optimization.js.
const { createRunPoller } = require('./ai-optimization');

// apiAction's axios call has no timeout: a hung request must not block the poll for ever. A request that has been
// pending this long is superseded by the next one and its late answer is dropped
const IN_FLIGHT_MAX_MS = 60000;

const NO_ANSWER = { ok: false, error: 'the server did not answer' };

// fetchSpend() -> promise of { ok, data } or { ok: false, error }; onOutcome(outcome) shows an answer; activityOf(outcome)
// is true (an AI run is queued or running), false, or null (leave the speed alone: a failed request); schedule is
// lib/timers' later(); fastMs and idleMs are the two intervals. start() fetches once at once; refresh() fetches at once
// (one follow-up after a pending request, never two at once); stop() releases the timer. A cancel of a later() timer
// from inside its own tick would still re-arm once, so the interval is only changed after the request has answered.
// subscribe(listener) -> unsubscribe is how something tells the poll that an AI run was just started (lib/ai-activity:
// the Start button now, Phase 9's Continue later): start() subscribes once and stop() unsubscribes. A notification counts
// as activity until the next answer decides the speed again, so an optimistic fast mode never sticks. doc is the page
// (visibilityState, addEventListener, removeEventListener), passed in by the component from inside its effect; null means
// always visible and no listener. start() adds one 'visibilitychange' listener and stop() removes it. A request already
// in flight when the tab hides still applies its answer, it just arms no timer
const createSpendPoll = ({
	fetchSpend,
	onOutcome,
	activityOf,
	schedule,
	fastMs,
	idleMs,
	now = Date.now,
	subscribe = () => () => {},
	doc = null,
}) => {
	let active = false;
	let started = false;
	let stopped = false;
	let inFlight = false;
	let inFlightSince = 0;
	let again = false;
	let poller = null;
	let unsubscribe = null;

	const isHidden = () => Boolean(doc) && doc.visibilityState === 'hidden';

	const applyCadence = () => {
		if (stopped) {
			return;
		}
		if (isHidden()) {
			poller.sync(false);
			return;
		}
		poller.setIntervalMs(active ? fastMs : idleMs);
		poller.sync(true);
	};

	const run = async force => {
		if (stopped || isHidden()) {
			return;
		}
		if (inFlight && now() - inFlightSince < IN_FLIGHT_MAX_MS) {
			if (force) {
				again = true;
			}
			return;
		}
		inFlight = true;
		inFlightSince = now();
		const seq = poller.begin();
		let outcome;
		try {
			outcome = await fetchSpend();
		} catch (error) {
			outcome = NO_ANSWER;
		}
		if (!poller.isCurrent(seq)) {
			return;
		}
		inFlight = false;
		onOutcome(outcome);
		const next = activityOf(outcome);
		if (next !== null) {
			active = next;
		}
		applyCadence();
		if (again) {
			again = false;
			run(true);
		}
	};

	// the event carries no state, so it is read from the page; a fresh interval starts from the moment the tab is visible
	// and two visible events in a row still leave one timer (applyCadence arms only after the cancel)
	const onVisibility = () => {
		if (stopped) {
			return;
		}
		if (isHidden()) {
			poller.sync(false);
			return;
		}
		run(true);
		poller.sync(false);
		applyCadence();
	};

	poller = createRunPoller({
		tick: () => {
			run(false);
		},
		schedule,
		intervalMs: idleMs,
	});

	return {
		start() {
			if (started || stopped) {
				return;
			}
			started = true;
			if (doc) {
				doc.addEventListener('visibilitychange', onVisibility);
			}
			unsubscribe = subscribe(() => {
				active = true;
				run(true);
				applyCadence();
			});
			run(true);
			applyCadence();
		},
		refresh() {
			run(true);
		},
		stop() {
			if (stopped) {
				return;
			}
			stopped = true;
			if (doc) {
				doc.removeEventListener('visibilitychange', onVisibility);
			}
			if (unsubscribe) {
				unsubscribe();
				unsubscribe = null;
			}
			poller.stop();
		},
		mode() {
			return active ? 'fast' : 'idle';
		},
		isScheduled() {
			return poller.isScheduled();
		},
	};
};

module.exports = { createSpendPoll, IN_FLIGHT_MAX_MS };
